import type { Moment } from 'moment';
import sortBy from 'lodash/sortBy';
import { ICalendarItemData } from 'Calendar/types';
import { momentTimezone } from 'utils/time';

/** Above this many appointments a merged cell falls back to the summary list box + popover. */
export const GRID_THRESHOLD = 4;

/**
 * Shrink factor applied to a card that shares its cell with other items. Applied to the card
 * element itself (`.AppointmentStyled` / `.BlockHourItemStyled`), never to a track or container,
 * so nesting a grid inside a split column cannot multiply the zoom.
 */
export const COMPACT_CARD_ZOOM = 0.7;

/** Time window a merged cell covers, as wall-clock minutes-of-day plus a length in minutes. */
export type CellRange = {
  /** Minutes since midnight of the cell's first row. */
  start: number;
  /** Cell length in minutes (rowSpan x row distance); always >= 1. */
  span: number;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** True when `count` items fit the side-by-side grid rather than the summary box. */
export const shouldUseGrid = (count: number) => count > 0 && count <= GRID_THRESHOLD;

/** Chronological order, by absolute start instant so equal wall-clock times stay stable. */
export const sortByStart = <T extends Pick<ICalendarItemData, 'startTime'>>(items: T[]): T[] =>
  sortBy(items, o => momentTimezone(o.startTime).valueOf());

/**
 * Wall-clock minutes since midnight. The master timeline (`rowTime`) and item times come from
 * different moment factories, so they are only comparable as wall-clock values — never as epochs.
 */
export const toMinutesOfDay = (value: Moment) => value.hours() * 60 + value.minutes();

/**
 * Position an item inside its cell as percentages of the cell's own row range: a 02:45-03:00 item
 * in a 02:15-03:45 cell starts one third down and is one sixth tall. Items reaching outside the
 * range are clamped to it rather than overflowing the cell.
 */
export const computeSlotPosition = (
  item: Pick<ICalendarItemData, 'startTime' | 'endTime'>,
  cellRange: CellRange,
) => {
  const span = Math.max(cellRange.span, 1);
  const startMoment = momentTimezone(item.startTime);
  const start = toMinutesOfDay(startMoment);
  // Length comes from the two instants, not from their minutes-of-day: an item running past
  // midnight would otherwise measure as a negative length and collapse to nothing.
  const duration = Math.max(momentTimezone(item.endTime).diff(startMoment, 'minutes'), 0);
  const top = clamp(((start - cellRange.start) / span) * 100, 0, 100);
  // An item reaching in from before the cell is measured from where it becomes visible, so it
  // cannot claim the height it spent outside the cell on top of the height it occupies inside it.
  const visibleStart = Math.max(start, cellRange.start);
  const height = clamp(((start + duration - visibleStart) / span) * 100, 0, 100 - top);
  return { top, height };
};
